import { createContext, useContext } from "react";
import type { AppContext, AppContextEncoded, ConnectionSummary, OpenNoteContext } from "@papersflow/contracts";
import type { Doc, Id } from "../../../backend/convex/_generated/dataModel";
import type { WorkspaceConnectionAccess } from "./workspaceConnectionAccess";

export interface ActiveViewProjection {
  readonly workspaceId: Id<"workspaces">;
  readonly projectId: Id<"projects">;
  readonly threadId: Id<"threads"> | null;
  readonly viewer: NonNullable<AppContext["viewer"]>;
  readonly runtime?: NonNullable<AppContext["runtime"]>;
  readonly artifact?: NonNullable<AppContextEncoded["artifact"]>;
  readonly selection?: NonNullable<AppContextEncoded["selection"]>;
  readonly openNote?: OpenNoteContext;
}

export interface ActiveViewProjectionAuthority {
  readonly workspaceId: Id<"workspaces">;
  readonly projectId: Id<"projects">;
  readonly threadId: Id<"threads"> | null;
  readonly viewId: string;
  readonly version: string;
  readonly publicationId: string;
}

export interface ActiveViewProjectionPublication {
  readonly authority: ActiveViewProjectionAuthority;
  readonly projection: ActiveViewProjection;
}

function activeViewProjectionAuthorityEquals(
  left: ActiveViewProjectionAuthority,
  right: ActiveViewProjectionAuthority,
): boolean {
  return (
    left.workspaceId === right.workspaceId &&
    left.projectId === right.projectId &&
    left.threadId === right.threadId &&
    left.viewId === right.viewId &&
    left.version === right.version &&
    left.publicationId === right.publicationId
  );
}

export function reconcileActiveViewProjection(
  current: ActiveViewProjectionPublication | null,
  authority: ActiveViewProjectionAuthority,
  projection: ActiveViewProjection | null,
): ActiveViewProjectionPublication | null {
  if (projection) {
    if (
      projection.workspaceId !== authority.workspaceId ||
      projection.projectId !== authority.projectId ||
      projection.threadId !== authority.threadId
    ) {
      return current;
    }
    return { authority, projection };
  }
  return current && activeViewProjectionAuthorityEquals(current.authority, authority)
    ? null
    : current;
}

export type WorkspaceThread = Doc<"threads"> & {
  readonly messageCount: number;
  readonly projectName: string;
  readonly pinnedAt?: number;
  readonly runStatus?: Doc<"agentRuns">["status"];
  readonly lastModel?: string;
};

export interface WorkspaceRouteScope {
  readonly workspaceId: Id<"workspaces">;
  readonly projectId?: Id<"projects">;
  readonly threadId?: Id<"threads">;
}

export interface WorkspaceContextValue {
  readonly workspaceName: string;
  readonly projectName: string;
  readonly activeWorkspace: Doc<"workspaces"> | undefined;
  readonly activeMembership: Doc<"memberships"> | undefined;
  readonly activeProject: Doc<"projects"> | undefined;
  readonly activeThread: WorkspaceThread | undefined;
  readonly projects: readonly Doc<"projects">[];
  readonly threads: readonly WorkspaceThread[];
  readonly artifacts: readonly Doc<"artifacts">[];
  readonly connectedServices: readonly ConnectionSummary[];
  readonly connectionAccess: WorkspaceConnectionAccess;
  readonly dataLoading: boolean;
  readonly canManageProjects: boolean;
  readonly canWriteThreads: boolean;
  readonly canWriteTasks: boolean;
  readonly currentUser:
    | {
        readonly id?: string;
        readonly name?: string | null;
        readonly email?: string | null;
        readonly image?: string | null;
      }
    | null
    | undefined;
  readonly signOut: () => Promise<void>;
  readonly selectRouteScope: (scope: WorkspaceRouteScope) => void;
  readonly selectProject: (projectId: Id<"projects">) => void;
  readonly selectThread: (threadId: Id<"threads">) => void;
  readonly ensurePersonalWorkspace: () => Promise<void>;
  readonly createProject: (
    name: string,
    description?: string,
    instructions?: string,
  ) => Promise<Id<"projects">>;
  readonly createThread: (projectId: Id<"projects">, title?: string) => Promise<Id<"threads">>;
  readonly renameThread: (threadId: Id<"threads">, title: string) => Promise<void>;
  readonly setThreadPinned: (threadId: Id<"threads">, pinned: boolean) => Promise<void>;
  readonly setActiveViewProjection: (
    authority: ActiveViewProjectionAuthority,
    projection: ActiveViewProjection | null,
  ) => void;
  readonly createSnapshot: (overrides?: Partial<AppContext>) => AppContext;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspaceContext(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspaceContext must be used inside WorkspaceContextProvider");
  return value;
}
